import { useTranslation } from "react-i18next";
import { useCallback, useMemo } from "react";
import { UseProductCardSelectorProps } from "./product-card-selector.types";
import {
  CartProduct,
  useAnalytics,
  useAppNavigate,
  makeUrl,
  useAppLocation,
  ToppingValueDto,
  ProductType,
  useInventory,
  TypeInventory,
  makeChatUrl,
} from "@commons-monorepo/index";
import { decamelizeKeys } from "humps";
import { productCartSelectorEvents } from "./events";

export const useProductCardSelectorLogic = ({
  product,
  offerId,
  cartProducts,
  addProduct,
  theme,
  storeMetadata,
}: UseProductCardSelectorProps) => {
  const { t } = useTranslation();
  const [logEvent] = useAnalytics({ storeMetadata });
  const navigate = useAppNavigate();
  const { hasProductTypeInTheCart, canAddProduct } = useInventory({
    cartProducts,
  });
  const { pathname } = useAppLocation();
  const payloadTheme = decamelizeKeys({
    templateId: theme.templateId,
    headerId: theme.template.HEADER,
    bannerId: theme.template.COVER,
    categoriesId: theme.template.CATEGORIES,
    cardId: theme.template.PRODUCT_CARD,
    footerId: theme.template.FOOTER,
    storeInfoId: theme.template.STORE_INFO.SKIN,
  });

  const hasToppings = !!product.toppings?.length;
  const isKit = !!product.kit;
  const requiredToppings = useMemo(() => product.toppings || [], [product]);
  const hasProductInTheCart = useMemo(
    (): boolean => !!cartProducts?.find((p) => p.dbId === product.id),
    [cartProducts, product],
  );

  const navigateToProductDetail = (urlSumer?: string): void => {
    if (!isKit) {
      if (urlSumer) {
        navigate(makeChatUrl(pathname, `producto/${product.id}`, urlSumer), {
          relative: "path",
        });
      } else {
        navigate(makeUrl(pathname, `producto/${product.id}`), {
          relative: "path",
        });
      }
      
    } else {
      if (urlSumer) {
        navigate(makeChatUrl(pathname, `combo/${product.friendlyUrl}`, urlSumer), {
          relative: "path",
        });
      } else {
        navigate(makeUrl(pathname, `combo/${product.friendlyUrl}`), {
          relative: "path",
        });
      }
    }
  };

  const handleAddProduct = (
    productId: string,
    selectedToppings?: ToppingValueDto[],
  ): void => {
    if (product.type === ProductType.Service) {
      const hasAddedService = !!cartProducts.find((cp) => cp.id === product.id);
      if (hasAddedService) return;
    }
    const payload: CartProduct = {
      id: productId,
      quantity: 1,
      toppings: selectedToppings || [],
      price: product.priceWithDiscount || product.price,
      wholesale: product.wholesale,
      type: product.type,
    } as CartProduct;
    if (offerId) payload.offerId = offerId;
    addProduct(payload);
  };

  const handleBuyButtonClick = (
    selectedToppings?: ToppingValueDto[],
    shouldNavigate = true,
    urlSumer?: string,
  ) => {
    if (
      (product.type === ProductType.Service &&
        (hasProductTypeInTheCart(ProductType.Product) ||
          hasProductTypeInTheCart(undefined))) ||
      ((product.type === ProductType.Product || product.type === undefined) &&
        hasProductTypeInTheCart(ProductType.Service))
    ) {
      return;
    }
    logEvent(
      productCartSelectorEvents.SELECT_ADD_PRODUCT,
      { ...payloadTheme },
      ["clevertap", "facebook", "appsflyer"],
    );
    if (product.type === ProductType.Service && !urlSumer) {
      navigate(makeUrl(pathname, `producto/${product.id}/agregar-servicio`), {
        relative: "path",
      });
      return;
    }
    if (product.type === ProductType.Service && urlSumer) {
      navigate(makeChatUrl(pathname, `producto/${product.id}/agregar-servicio`, urlSumer), {
        relative: "path",
      });
      return;
    }
    if (isKit) {
      navigateToProductDetail(urlSumer);
      return;
    }
    if (hasToppings && !!requiredToppings.length) {
      if (selectedToppings?.length === requiredToppings?.length) {
        handleAddProduct(product.id, selectedToppings);
      } else if (shouldNavigate) {
        navigateToProductDetail(urlSumer)};
    } else handleAddProduct(product.id);
  };

  const canProductToBeAdded = useCallback(
    (selectedToppings?: ToppingValueDto[]) => {
      return (
        canAddProduct(product) ||
        (!!requiredToppings.length
          ? selectedToppings?.length === requiredToppings.length
          : true)
      );
    },
    [canAddProduct, requiredToppings, product],
  );

  const productIsInventoryAvailable = useMemo(() => {
    return (
      product.inventory?.type !== TypeInventory.Limited ||
      (product.inventory?.type === TypeInventory.Limited &&
        product.inventory.units > 0)
    );
  }, [product]);

  return {
    t,
    handleBuyButtonClick,
    handleAddProduct,
    hasToppings,
    requiredToppings,
    canProductToBeAdded,
    hasProductInTheCart,
    productIsInventoryAvailable,
  };
};
